import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'

// Types
export interface LeadPage {
  id: string
  pageId: string
  name: string
  picture: string
  total: number
  valid: number
  invalid: number
  pending: number
  twentyFourH: number
  lastScan: LeadJobRecord | null
  lastQualify: LeadJobRecord | null
}

export interface LeadJobRecord {
  id: number
  type: 'scan' | 'qualify'
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled'
  total: number
  processed: number
  imported: number
  invalid: number
  started_at: string | null
  completed_at: string | null
}

export interface LeadStatus {
  pageId: string
  scan?: {
    status: string
    imported: number
    updated: number
    pages: number
    started_at?: string
  }
  qualify?: {
    status: string
    valid: number
    invalid: number
    processed: number
    total: number
    started_at?: string
  }
}

export interface LeadContact {
  id: number
  page_id: string
  psid: string
  name: string
  source: string
  status: 'valid' | 'invalid' | 'pending'
  first_seen_at: string
  last_seen_at: string
  message_count: number
  qualified_at: string | null
}

export interface PaginatedContacts {
  data: LeadContact[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// Hooks
export function useLeadsPages(refetchInterval: number | false = false) {
  return useQuery<LeadPage[]>({
    queryKey: ['leads', 'pages'],
    queryFn: () => api.get('/leads/pages'),
    refetchInterval,
  })
}

export function useLeadsStatus(refetchInterval: number | false = false) {
  return useQuery<LeadStatus[]>({
    queryKey: ['leads', 'status'],
    queryFn: () => api.get('/leads/status'),
    refetchInterval,
  })
}

export function useLeadsContacts(pageId: string | null, params: {
  page?: number
  status?: string
  search?: string
  source?: string
}) {
  return useQuery<PaginatedContacts>({
    queryKey: ['leads', 'contacts', pageId, params],
    queryFn: () => {
      const searchParams = new URLSearchParams()
      if (params.page) searchParams.set('page', String(params.page))
      if (params.status) searchParams.set('status', params.status)
      if (params.search) searchParams.set('search', params.search)
      if (params.source) searchParams.set('source', params.source)
      const qs = searchParams.toString()
      return api.get(`/leads/contacts/${pageId}${qs ? '?' + qs : ''}`)
    },
    enabled: !!pageId,
  })
}

export function useScanAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ dispatched: number; skipped: number; cooldownSkipped: number; cooldownHours: number }>('/leads/scan-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', 'status'] })
      qc.invalidateQueries({ queryKey: ['leads', 'pages'] })
    },
  })
}

export function useScanPage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pageId: string) => api.post(`/leads/scan/${pageId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', 'status'] })
    },
  })
}

export function useQualifyAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/leads/qualify-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', 'status'] })
      qc.invalidateQueries({ queryKey: ['leads', 'pages'] })
    },
  })
}

export function useQualifyPage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pageId: string) => api.post(`/leads/qualify/${pageId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', 'status'] })
    },
  })
}

export function useCancelJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pageId: string) => api.post(`/leads/cancel/${pageId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', 'status'] })
      qc.invalidateQueries({ queryKey: ['leads', 'pages'] })
    },
  })
}
